import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Badge,
  Button,
  Container,
  Popover,
  PopoverBody,
  PopoverHeader,
  UncontrolledPopover,
  UncontrolledTooltip,
} from "reactstrap"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import {
  formatAmount,
  getICDAgreementsStatus,
  getTableSortingHeaderIcon,
  handleDownload,
} from "helpers/common"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import { Link } from "react-router-dom"
import eye from "../../assets/images/icons/eye.svg"
import downloadIcon from "../../assets/images/downloadGrey.svg"
import {
  useApproveInvestorsAgreementMutation,
  useLazyGetInvestorsAgreementsQuery,
  useRejectInvestorsAgreementMutation,
} from "store/api/investors/investors.api"
import SchedulesModel from "modals/schedulesModel"
import ApproveWithSignerModal from "modals/ApproveWithSignerModal"
import RejectWithMessageModal from "modals/rejectWithMessageModal"
import OutsideClickPopover from "modals/OutsideClickPopover"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
  redirection_url: window.location.href,
}

const Agreements = ({ type }) => {
  const [getResults, { isFetching }] = useLazyGetInvestorsAgreementsQuery()
  const [onApprove, { isLoading: isApproving }] =
    useApproveInvestorsAgreementMutation()
  const [onReject, { isLoading: isRejecting }] =
    useRejectInvestorsAgreementMutation()
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [filters, setFilters] = useState(defaultFilters)
  const { hasPermission } = usePermission()
  const [scheduleModel, setScheduleModel] = useState(false)
  const scheduleRef = useRef(null)
  const [isToast, setIsToast] = useState(false)
  const [agreementData, setAgreementData] = useState(null)
  const [actionType, setActionType] = useState("")

  //meta title
  document.title = "Agreements - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults({ ...filters, type }).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters, type])

  const columns = useMemo(() => {
    const data = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        id: "created_at",
        accessorFn: (row) =>
          row?.created_at ? new Date(row.created_at).getTime() : null,
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.formatted_created_at,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <DefaultAvatar
            avatar_url={
              type === "INVESTOR"
                ? row?.original.investor?.avatar_url
                : row?.original.wealthPartner?.avatar_url
            }
            email={
              type === "INVESTOR"
                ? row?.original.investor?.email
                : row?.original.wealthPartner?.email
            }
            name={
              type === "INVESTOR"
                ? row?.original.investor?.name
                : row?.original.wealthPartner?.name
            }
          />
        ),
      },
      {
        header: "Reference No.",
        accessorKey: "referenceNo",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <>{row?.original.agreement_ref || "Not Applicable"}</>
        ),
      },
      ...(type === "INVESTOR"
        ? [
            {
              header: "Amount(₹)",
              accessorKey: "amount",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => <>₹ {formatAmount(row?.original.amount)}</>,
            },
            {
              header: "Remaining Amount(₹)",
              accessorKey: "remaining_amount",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => (
                <>₹ {formatAmount(row?.original.remaining_amount)}</>
              ),
            },
            {
              header: "Expiration Date",
              accessorKey: "formatted_expiration_date",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => <>{row?.original.formatted_expiration_date}</>,
            },
            {
              header: "Document Type",
              accessorKey: "doc_type",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }) => (
                <div className="code-status">
                  <div className="badge counteroffer">
                    <span></span> ICD Master Agreement
                  </div>
                </div>
              ),
            },
            {
              header: "Schedules",
              accessorKey: "unique_code",
              enableColumnFilter: false,
              enableSorting: false,
              cell: ({ row }) => (
                <div className="d-flex gap-2 justify-content-center align-items-center">
                  <Badge color="primary">
                    {row?.original?.icdSchedules?.length}
                  </Badge>
                  {row?.original?.icdSchedules?.length > 0 && (
                    <div
                      onClick={() => {
                        scheduleRef.current = row?.original?.icdSchedules
                        setScheduleModel(true)
                      }}
                    >
                      <a href={"#"}>View</a>
                    </div>
                  )}
                </div>
              ),
            },
          ]
        : []),
      {
        header: () => <div className="flex items-center gap-2">Status</div>,
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getICDAgreementsStatus(row?.original?.status)}
          </div>
        ),
      },
      // ...(hasPermission("AUDIT_TRAIL")
      // ? [
      {
        header: "Audit Trail",
        id: "audit_trail",
        cell: ({ row }) => {
          const viewAuditTooltip = `viewAuditTooltip-${row?.original?.id}`
          return (
            <>
              {["SUCCESS"].includes(row?.original?.status) ? (
                <div className="d-flex justify-content-center align-items-center flex-row gap-2">
                  {/* <a href={row.original.doc}>Sign Now</a> */}
                  <div className="action-view">
                    <Link
                      to={row.original.audit_doc_url}
                      target="_blank"
                      id={viewAuditTooltip}
                    >
                      <img src={eye} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={viewAuditTooltip}
                      >
                        View Audit Trail
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                </div>
              ) : (
                "Not Applicable"
              )}
            </>
          )
        },
      },
      //   ]
      // : []),

      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const viewTooltip = `viewTooltip-${row?.original?.id}`
          const downloadTooltip = `downloadTooltip-${row?.original?.id}`
          const rejectedTooltip = `rejectedTooltip-${row?.original?.id}`
          return (
            <>
              {["SUCCESS"].includes(row?.original?.status) ? (
                <div className="d-flex justify-content-center align-items-center flex-row gap-2">
                  {/* <a href={row.original.doc}>Sign Now</a> */}
                  <div className="action-view">
                    <Link
                      to={row.original.doc_url}
                      target="_blank"
                      id={viewTooltip}
                    >
                      <img src={eye} alt="" />
                      <UncontrolledTooltip placement="top" target={viewTooltip}>
                        View
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                  <div className="action-view">
                    <Link
                      to={"#"}
                      id={downloadTooltip}
                      onClick={() =>
                        handleDownload(
                          row.original.doc_url,
                          row?.original.agreement_ref || "ICD Master Agreement"
                        )
                      }
                    >
                      <img src={downloadIcon} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={downloadTooltip}
                      >
                        Download
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {["PENDING"].includes(row?.original?.status) ? (
                    <div className="d-flex flex-column gap-1 buttons">
                      <Button
                        type="button"
                        className="approve"
                        onClick={() => {
                          setIsToast(true)
                          setActionType("APPROVE")
                          setAgreementData(row?.original)
                        }}
                        style={{
                          minWidth: "130px",
                          maxWidth: "130px",
                          padding: "6px 25px",
                          textAlign: "center",
                        }}
                      >
                        Approve
                      </Button>

                      <Button
                        type="button"
                        className="reject"
                        size="sm"
                        onClick={() => {
                          setIsToast(true)
                          setActionType("REJECT")
                          setAgreementData(row?.original)
                        }}
                        style={{
                          minWidth: "130px",
                          maxWidth: "130px",
                          padding: "6px 25px",
                          textAlign: "center",
                        }}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <>
                      {["REJECTED"].includes(row?.original?.status) ? (
                        <Link to={"#"} id={rejectedTooltip}>
                          <i
                            className="mdi mdi-information-outline"
                            style={{ color: "#7a7a7a", fontSize: "17px" }}
                          />
                          <OutsideClickPopover
                            target={rejectedTooltip}
                            title="Reason for Rejection"
                            body={row?.original?.reject_reason}
                          />
                        </Link>
                      ) : (
                        <>Not Applicable</>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )
        },
      },
    ]

    const canAccess = hasPermission(PERMISSIONS.ICD_VIEW)
    if (canAccess) return data
    return data.filter((item) => item.id !== "actions")
  }, [hasPermission])

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={"Agreements"} />

          <TableContainer
            isLoading={isFetching}
            count={count}
            filters={filters}
            setFilters={setFilters}
            columns={columns}
            data={data}
            isGlobalFilter={true}
            isCustomPageSize={true}
            // isGlobalFilter={true}
            isPagination={true}
            pagination="pagination"
            paginationWrapper="dataTables_paginate paging_simple_numbers"
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </Container>
        {scheduleModel && scheduleRef?.current && (
          <SchedulesModel
            open={scheduleModel}
            close={() => {
              setScheduleModel(false)
              scheduleRef.current = null
            }}
            data={scheduleRef.current}
          />
        )}
        {agreementData && isToast && actionType === "APPROVE" && (
          <ApproveWithSignerModal
            open={isToast}
            data={{ id: agreementData.id }}
            setOpen={setIsToast}
            isLoading={isApproving}
            title="Approve Agreement"
            description="Are you sure you want to approve this agreement?"
            confirmButtonText="Approve Agreement"
            onClose={() => {
              setIsToast(false)
              setActionType("")
              setAgreementData(null)
            }}
            onApprove={onApprove}
            onSuccess={() => {
              getList()
            }}
          />
        )}

        {agreementData && isToast && actionType === "REJECT" && (
          <RejectWithMessageModal
            open={isToast}
            data={{ id: agreementData.id }}
            setOpen={setIsToast}
            isLoading={isRejecting}
            title="Reject Agreement"
            description="Are you sure you want to reject this agreement?"
            onClose={() => {
              setIsToast(false)
              setActionType("")
            }}
            onReject={onReject}
            onSuccess={() => {
              getList()
            }}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default Agreements
